/**
 * Pure models helper functions extracted from Generate.js and helpers/index.js.
 * These are testable without React context or component state.
 */

import { methods } from '../constants';
import hostConfig from '../utils/config';

/**
 * Get the default model for a given method.
 * Checks story-level overrides first, then falls back to the methods constant.
 *
 * @param {string} method – e.g. 'text2vid', 'vid2vid'
 * @param {Object|Array} [defaultModels=[]] – story-level model overrides keyed by method
 * @returns {string|null} – model key, e.g. 'text2vid-seedance-2-0-8sec'
 */
export const getDefaultModelForMethod = (method, defaultModels = []) => {
  if (!method) return null;
  return (defaultModels && defaultModels[method])
    ? defaultModels[method]
    : methods.get(method).defaultModel;
};

/**
 * Get models available for a given method, excluding disallowed ones.
 *
 * @param {string} method – method key
 * @param {string[]} [disallowedModels=[]] – model keys to exclude
 * @returns {Array<{ key: string, data: object }>}
 */
export const getAvailableModelsForMethod = (method, disallowedModels = []) => {
  const models = hostConfig.aiModels;
  const modelsArr = Array.from(models, ([key, data]) => ({ key, data }));
  if (method) return modelsArr.filter(item => item.data.method === method && !disallowedModels.includes(item.key));
  return modelsArr.filter(item => !disallowedModels.includes(item.key));
};

/**
 * Compute the total allowed references for a model + method combination.
 *
 * @param {string} modelKey – the model key
 * @param {string} method – the method key
 * @returns {number} – total allowed refs (maxRefs + 1 if method requires media)
 */
export const computeTotalRefs = (modelKey, method) => {
  const models = hostConfig.aiModels;
  const modelData = models.get(modelKey);
  if (!modelData) return 0;
  const methodData = methods.get(method);
  if (!methodData) return modelData.maxRefs || 0;
  const max = modelData.maxRefs || 0;
  return max + (methodData.requiresMedia ? 1 : 0);
};

/**
 * Determine whether the current model needs to switch to accommodate a
 * new reference being added, and if so, return the new model key.
 * Returns false if no model in the method can accommodate the ref count.
 *
 * This mirrors the logic from helpers/index.js handleModelLimit.
 *
 * @param {number}   newTotal – total refs needed (including the new one)
 * @param {string}   currentModel – current model key
 * @param {string}   method – method key
 * @param {string[]} [disallowedModels=[]] – model keys excluded from selection
 * @returns {string|false} – new model key, or false if blocked
 */
export const resolveModelForRefs = (newTotal, currentModel, method, disallowedModels = []) => {
  const models = hostConfig.aiModels;
  const modelData = models.get(currentModel);
  if (!modelData) return false;

  const availableModels = getAvailableModelsForMethod(method, disallowedModels);
  const filtered = availableModels.filter(
    item => item.data.method === method && (item.data.maxRefs || 0) >= newTotal
  );

  if (filtered.length === 0) return false;

  // Prefer same family
  const family = filtered.find(item => item.data.family && item.data.family === modelData.family);
  return family ? family.key : filtered[0].key;
};

/**
 * Resolves a model key (active or deprecated) to an active model key in aiModels.
 * 1. Returns modelKey directly if it is currently active.
 * 2. Checks `deprecate_replace` arrays across all active models.
 * 3. Supports transitive/chained deprecations (old -> intermediate -> current).
 * 4. Falls back to story-level default or method default if unmapped.
 *
 * @param {string} modelKey – the model key to resolve
 * @param {string} [fallbackMethod] – optional method for default fallback
 * @param {Object|Array} [defaultModels=[]] – story-level default models
 * @returns {string|null} – active model key
 */
export const resolveModelKey = (modelKey, fallbackMethod = null, defaultModels = [], visited = new Set()) => {
  if (!modelKey) {
    return fallbackMethod ? getDefaultModelForMethod(fallbackMethod, defaultModels) : null;
  }

  const models = hostConfig.aiModels;
  if (models && models.has(modelKey)) return modelKey;

  // Prevent circular resolution loops
  if (visited.has(modelKey)) {
    return fallbackMethod ? getDefaultModelForMethod(fallbackMethod, defaultModels) : null;
  }
  visited.add(modelKey);

  // Search active models for deprecate_replace match
  if (models) {
    for (const [key, data] of models.entries()) {
      if (Array.isArray(data.deprecate_replace) && data.deprecate_replace.includes(modelKey)) {
        if (models.has(key)) {
          return key;
        }
        return resolveModelKey(key, fallbackMethod, defaultModels, visited);
      }
    }
  }

  // Graceful fallback if no deprecation mapping found
  return fallbackMethod ? getDefaultModelForMethod(fallbackMethod, defaultModels) : null;
};

/**
 * Returns the default vid2vid model key designated by `composeVideoModel: true`.
 * Falls back to story-level default or vid2vid method default if none is explicitly flagged.
 *
 * @param {Object|Array} [defaultModels=[]] – story-level default models
 * @returns {string|null} – model key
 */
export const getComposeVideoModel = (defaultModels = []) => {
  const models = hostConfig.aiModels;
  if (models) {
    for (const [key, data] of models.entries()) {
      if (data.method === 'vid2vid' && data.composeVideoModel === true) {
        return key;
      }
    }
  }
  return getDefaultModelForMethod('vid2vid', defaultModels) || methods.get('reimagine')?.defaultModel || 'vid2vid-grok';
};

